import { useProgressStore } from '@/stores/modules/progressStore';
import type { StoreState } from '@/types/storeTypes';
import { STANDARD_SEQUENCE } from '@/constants/flows';

interface Address {
  street?: string;
  town?: string;
  postcode?: string;
  residentialStatusLookup?: {
    id: number;
  };
  residentialStatusId?: number;
  yearsAtAddress?: number;
  monthsAtAddress?: number;
}

/**
 * Calculates total time at addresses in years
 * @param addresses Array of address objects
 * @returns Total time in years
 */
const calculateTotalAddressTime = (addresses: Address[]): number => {
  return addresses.reduce((total, address) => {
    const years = address.yearsAtAddress || 0;
    const months = address.monthsAtAddress || 0;
    return total + years + (months / 12);
  }, 0);
};

/**
 * Checks if an address has all required fields
 * @param address Address object to check
 * @returns boolean indicating if address is complete
 */
const isAddressComplete = (address: Address, isCurrentAddress: boolean): boolean => {
  const baseComplete = !!(address.street && address.town && address.postcode);

  if (!isCurrentAddress) {
    return baseComplete;
  }

  return (
    baseComplete &&
    !!(
      (address.residentialStatusLookup?.id && address.residentialStatusLookup.id > 0) ||
      (address.residentialStatusId && address.residentialStatusId > 0)
    )
  );
};

/**
 * Gets pages that need to be completed based on pre-populated data
 * @param formData The pre-populated form data
 * @returns Array of page names that need to be completed
 */
const getPagesToComplete = (formData: any): string[] => {
  console.log('getPagesToComplete - Start', { formData });

  const addresses = formData.addresses || [];
  const totalAddressTime = calculateTotalAddressTime(addresses);
  const hasIncompleteAddresses = addresses.some(
    (address: Address, index: number) => !isAddressComplete(address, index === 0)
  );
  const firstEmployment = formData.employments?.[0];

  // Check if employment is complete based on status
  const employmentStatusId = firstEmployment?.employmentStatusLookup?.id || firstEmployment?.statusId;
  const isEmploymentComplete = firstEmployment && employmentStatusId && (
    // For self-employed (10) and other non-employer statuses, don't require occupation
    employmentStatusId === 10 || employmentStatusId === 12 || employmentStatusId === 13 || 
    employmentStatusId === 16 || employmentStatusId === 17 || employmentStatusId === 19 || 
    employmentStatusId === 21 || firstEmployment.occupation
  );

  // Check if employment-location is complete based on status
  const isEmploymentLocationComplete = firstEmployment && employmentStatusId && (
    // For self-employed (10), only require employerTown
    (employmentStatusId === 10 && !!firstEmployment.employerTown) ||
    // For other statuses that don't require employer details, mark as complete
    (employmentStatusId === 12 || employmentStatusId === 13 || employmentStatusId === 16 || 
     employmentStatusId === 17 || employmentStatusId === 19 || employmentStatusId === 21) ||
    // For regular employment, require both employerName and employerTown
    (!!firstEmployment.employerName && !!firstEmployment.employerTown)
  );

  // Check if employment length is complete (at least 1 year total across all employments)
  const totalEmploymentHistory = (formData.employments || []).reduce((total: number, employment: any) => {
    const years = employment.yearsAtEmployer || 0;
    const months = employment.monthsAtEmployer || 0;
    return total + years + (months / 12);
  }, 0);
  const isEmploymentLengthComplete = totalEmploymentHistory >= 1;

  // Check if employment income is complete (netMonthlyIncome is required)
  const isEmploymentIncomeComplete = !!formData.netMonthlyIncome;

  const completedPages = new Set([
    ...(formData.drivingLicenceTypeId || formData.drivingLicence?.id ? ['driving-licence'] : []),
    ...(formData.maritalStatusId || formData.maritalStatus?.id ? ['marital-status'] : []),
    ...(formData.dateOfBirth ? ['date-of-birth'] : []),
    ...(totalAddressTime >= 3 && !hasIncompleteAddresses ? ['address-add', 'address-status', 'address-length'] : []),
    ...(isEmploymentComplete ? ['employment'] : []),
    ...(isEmploymentLocationComplete ? ['employment-location'] : []),
    ...(isEmploymentLengthComplete ? ['employment-length'] : []),
    ...(isEmploymentIncomeComplete ? ['employment-income'] : []),
    ...(formData.loanAmount ? ['amount-to-finance'] : []),
    ...(formData.loanAmount && formData.vehicleType?.id ? ['finance-details'] : []),
    ...(formData.firstName && formData.lastName && (formData.title?.id || formData.titleId) ? ['contact-details-p1'] : []),
    ...(formData.email && formData.contactNumber && (formData.agreedTermsAndConditions || formData.agreedToTermsAndConditions) ? ['contact-details-p2'] : [])
  ]);

  const filteredPages = STANDARD_SEQUENCE.filter(page => !completedPages.has(page));
  
  // Always include pre-pop-landing as the first page for prepopulated forms
  const pagesToComplete = ['pre-pop-landing', ...filteredPages];

  console.log('getPagesToComplete - Result', { 
    pagesToComplete,
    employmentDetails: {
      isEmploymentComplete,
      isEmploymentLocationComplete,
      isEmploymentLengthComplete,
      isEmploymentIncomeComplete,
      employmentStatusId,
      totalEmploymentHistory,
      netMonthlyIncome: formData.netMonthlyIncome
    }
  });
  return pagesToComplete;
};

/**
 * Sets completed pages based on pre-populated data
 * @param formData The pre-populated form data
 * @param progressStore The progress store instance
 */
const setCompletedPagesFromPrepop = (formData: any, progressStore: any) => {
  console.log('setCompletedPagesFromPrepop - Start', { formData });
  
  const pagesToComplete = getPagesToComplete(formData);
  const completedPages = STANDARD_SEQUENCE.filter(page => !pagesToComplete.includes(page));

  progressStore.setPagesToComplete(pagesToComplete);
  progressStore.setCompletedPages(completedPages);
  
  console.log('setCompletedPagesFromPrepop - After setting pages', {
    completedPages: progressStore.completedPages,
    pagesToComplete: progressStore.pagesToComplete
  });
};

/**
 * Composable for managing pre-populated form state
 */
export function usePrepopFormState() {
  const progressStore = useProgressStore();

  return {
    setCompletedPagesFromPrepop: (formData: StoreState['form']) => setCompletedPagesFromPrepop(formData, progressStore)
  };
} 